/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'

import Button from '../ts-components/Button'
import ImportCSVBanner from './ImportCSVBanner'
import ImportCSVError from './ImportCSVError'
import ImportCSVFailedRowsLink from './ImportCSVFailedRowsLink'
import type { ImportReport } from './utils/importReport'
import { importReportNotice } from './utils/importReportNotice'

interface ImportCSVReportProps {
  report: ImportReport
  /** Opens the duplicate review the report is waiting on. */
  onReviewDuplicates: () => void
}

/**
 * SN-4838 — an import modal's account of its last 409: what was and was not
 * imported, "Review Duplicates" when the duplicates still wait, the failed-rows
 * download, and the problem rows by line.
 */
const ImportCSVReport = ({
  report,
  onReviewDuplicates,
}: ImportCSVReportProps) => {
  'use memo'
  const notice = importReportNotice(report)

  return (
    <>
      {notice.banners.map((banner) => (
        <ImportCSVBanner
          key={`${banner.type}:${banner.message}`}
          type={banner.type}
          message={banner.message}
        />
      ))}
      {notice.offerReview && (
        <div css={reviewStyles}>
          <Button
            variant='primary'
            text={capitalizeWords(t('common:importReportReviewDuplicates', {}))}
            onClick={onReviewDuplicates}
          />
        </div>
      )}
      {report.failedRowsFile && (
        <ImportCSVFailedRowsLink
          csv={report.failedRowsFile.rows}
          fileName={report.failedRowsFile.name}
        />
      )}
      {report.problems.length > 0 && (
        <ImportCSVError errors={report.problems} />
      )}
    </>
  )
}

export default ImportCSVReport

// ---- styles ----

const reviewStyles = css`
  display: flex;
  margin-bottom: 16px;
`
